/**
 * The Genre Mixer's genre selection card, plus the blend-compatibility readout.
 *
 * The two required genres sit in a 2-column grid and the optional third spans the
 * full width below them — verbatim from the prototype, including the way the grid's
 * bottom margin collapses to 0 while no compatibility row is showing.
 *
 * SIMULATED: the score is a hash of the two genre names, not an analysis. See
 * genreCompatibility() in src/lib/music.ts.
 */
import { Select } from '../forms';
import { CompatibilityRing } from '../studio';
import { GM_GENRES } from '../../data/music';
import { c } from '../../styles/theme';
import type { GenreCompatibility } from '../../types/domain';

/** The three genre slots this card edits. */
type GenreKey = 'genre1' | 'genre2' | 'genre3';

export interface MixerGenrePickerProps {
  genre1: string;
  genre2: string;
  genre3: string;
  /** Null until both required genres are chosen; hides the compatibility row. */
  compatibility: GenreCompatibility | null;
  onChange: (key: GenreKey, value: string) => void;
}

export default function MixerGenrePicker({
  genre1,
  genre2,
  genre3,
  compatibility,
  onChange,
}: MixerGenrePickerProps) {
  return (
    <div
      style={{
        background: c.surface1,
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 18,
      }}
    >
      {/* Panel eyebrow. text2 — see contrast.test.ts. */}
      <p
        style={{
          fontSize: 12,
          color: c.text2,
          margin: '0 0 10px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Genre mixer
      </p>
      <div className="lyx-grid-2" style={{ marginBottom: compatibility ? 16 : 0 }}>
        <Select
          label="Genre 1"
          value={genre1}
          onChange={(v) => onChange('genre1', v)}
          options={GM_GENRES}
          placeholder="Select a genre"
        />
        <Select
          label="Genre 2"
          value={genre2}
          onChange={(v) => onChange('genre2', v)}
          options={GM_GENRES}
          placeholder="Select a genre"
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <Select
          label="Genre 3 (optional)"
          value={genre3}
          onChange={(v) => onChange('genre3', v)}
          options={GM_GENRES}
          placeholder="None"
        />
      </div>

      {compatibility && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${c.border}`,
          }}
        >
          <CompatibilityRing score={compatibility.score} />
          <div>
            <p
              style={{
                fontSize: 11,
                color: c.text2,
                margin: '0 0 2px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Blend compatibility
            </p>
            <p
              style={{
                fontWeight: 600,
                fontSize: 16,
                margin: '0 0 2px',
                fontFamily: 'Space Grotesk',
              }}
            >
              {compatibility.score}%
            </p>
            <p style={{ fontSize: 12, color: c.accent, margin: 0 }}>{compatibility.label}</p>
          </div>
        </div>
      )}
    </div>
  );
}
